import CustomText from "@components/CustomText";
import EmptySearchList from "@components/SearchHeader/EmptySearchList";
import color from "@constants/color";
import { meta } from "@constants/cplp";
import { colors } from "@constants/theme";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import HeaderWithSearch from "@screens/MyDeliveries/HeaderWithSearch";
import NoBag from "@assets/svg/NoBag";
import {
  addToBag,
  getDeliveryOrderItems,
  markAction,
  markMyDeliveyModalAction,
} from "@services/api";
import React, { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  SectionList,
  StyleSheet,
  View,
} from "react-native";
import MarkingInfoModal from "@screens/MyDeliveries/components/MarkingInfoModal";
// eslint-disable-next-line import/no-named-as-default
import t from "@constants/localization";
import CrateItem from "@screens/CustomerOrderItems/components/CrateCard";
// @ts-expect-error - lodash types not available
import { debounce } from "lodash";
import InfoLines from "@screens/CustomerOrderItems/components/InfoModal/InfoLines";
import BottomButton from "@screens/MyDeliveries/SortingDeliveries/components/BottomButton";
import AddBagNoModal from "@screens/MyDeliveries/SortingDeliveries/components/AddBagNoModal";
import InfoModal from "@screens/CustomerOrderItems/components/InfoModal";
import ShipmentCard from "@screens/CustomerOrderItems/components/ShipmentCard";
import {
  checkCanMarkAllQuantity,
  checkIfAllDefectiveForTaskPage,
} from "@screens/MyDeliveries/utils";
import type {
  OrderItem,
  ImagesMandatoryForSamasya,
} from "@screens/CustomerOrderItems/interface";

interface RouteParams {
  orders_list?: string[];
  spokeName?: string;
  user_id?: string;
  date?: string;
  source?: string;
  shipment_ids?: string[];
  route_id?: string;
  bags?: string[];
  type?: string;
  bags_disabled?: boolean;
  cx_packing_enabled?: boolean;
  route_name?: string;
  db_name?: string;
  status?: string;
  user_name?: string;
}

interface Route {
  params?: RouteParams;
}

interface Navigation {
  pop: () => void;
  goBack?: () => void;
}

interface SortingItemListProps {
  navigation: NavigationProp<Record<string, unknown>> & Navigation;
  route: Route;
}

interface ItemGroup {
  type:
    | "CRATE"
    | "OTHER_CRATE"
    | "SHIPMENT"
    | "SKU_WISE_ORDER_ITEMS"
    | "PACKED_ORDERS";
  items: OrderItem[];
  display_type?:
    | "CRATE"
    | "OTHER_CRATE"
    | "SHIPMENT"
    | "SKU_WISE_ORDER_ITEMS"
    | "PACKED_ORDERS";
  id?: string;
}

interface SectionData extends ItemGroup {
  data: OrderItem[];
}

interface MetaData {
  InfoModal?: {
    title?: string;
    list?: string[];
    imageUrl?: string;
  };
  ConfirmSubmitModal?: {
    title?: string;
    list?: string[];
    imageUrl?: string;
  };
}

function getCrateOrShipmentItem(
  type: "CRATE" | "OTHER_CRATE" | "SHIPMENT" | string,
): typeof CrateItem | typeof ShipmentCard {
  return type === "PACKED_ORDERS" || type === "CRATE" || type === "OTHER_CRATE"
    ? CrateItem
    : ShipmentCard;
}

function SortingItemList({ navigation, route }: SortingItemListProps) {
  const getDeliveryOrderItemsApi = getDeliveryOrderItems;

  const isFocused = useIsFocused();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ordersItem, setOrdersItem] = useState<ItemGroup[]>([]);
  const [fullOrdersItem, setFullOrdersItem] = useState<ItemGroup[]>([]);
  const [markingConfig, setMarkingConfig] = useState<unknown[]>([]);
  const [markingConfigs, setMarkingConfigs] = useState<
    Record<string, unknown[]>
  >({});
  const [meteData, setMetaData] = useState<MetaData>({});
  const [searchEnable] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showConfirmSubmitModal, setShowConfirmSubmitModal] = useState(false);
  const [infoItemState, setInfoItemState] = useState<string | null>(null);
  const [buttonPress, setButtonPress] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [showBagModal, setShowBagModal] = useState(false);
  const [bagModalError, setBagModalError] = useState("");
  const [bagBtnLoading, setBagBtnLoading] = useState(false);
  const [isOfdCp, setIsOfdCp] = useState(false);
  const [plasticBagCount, setPlasticBagCount] = useState(0);
  const [imagesMandatoryForSamasya, setImagesMandatoryForSamasya] = useState<
    ImagesMandatoryForSamasya | Record<string, never>
  >({});

  const PATH = "screens/MyDeliveries/SortingDeliveries/sortingItemList";

  useFocusEffect(
    useCallback(() => {
      if (isFocused || showBagModal) {
        fetchData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused, showBagModal]),
  );

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data } = await getDeliveryOrderItemsApi({
        order_ids: route?.params?.orders_list,
        spoke_name: route?.params?.spokeName || "",
        user_id: route?.params?.user_id,
        date: route?.params?.date || "",
        source: route?.params?.source,
      });
      setOrdersItem(data?.data);
      setMarkingConfig(data?.marking_config);
      setMarkingConfigs(
        data?.marking_configs || { default: data?.marking_config || [] },
      );
      setMetaData(data?.meta);
      setFullOrdersItem(data?.data);
      setShowConfirmSubmitModal(data?.show_confirmSubmitModal);
      setInfoItemState(data?.item_info_state);
      setShowSubmit(data?.showSubmitButton);
      setIsOfdCp(data?.is_ofd_cp);
      setPlasticBagCount(data?.plastic_bag_count || 0);
      setImagesMandatoryForSamasya(data?.images_mandatory_for_samasya || {});
    } catch {
      // Error handling
    } finally {
      setIsRefreshing(false);
      setIsLoading(false);
    }
  };

  const onRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  const sortUpdates = useMemo<SectionData[]>(() => {
    return ordersItem.map((v) => {
      return {
        ...v,
        data: v.items || [],
      };
    });
  }, [ordersItem]);
  const handleAddToBag = async (bags: string[]) => {
    const order_item_ids: string[] = [];
    ordersItem.forEach((v) => {
      if (v?.items && Array.isArray(v.items)) {
        v.items.forEach((i) => {
          if (i?.order_item_id) {
            order_item_ids.push(i.order_item_id);
          }
        });
      }
    });
    const shipment_ids: string[] = [];
    ordersItem.forEach((v) => {
      if (v?.items && Array.isArray(v.items)) {
        v.items.forEach((i) => {
          if (i?.crate_shipment_id) {
            shipment_ids.push(i.crate_shipment_id);
          }
        });
      }
    });
    const newBags = Array.from(new Set(bags.filter((v: string) => v !== "")));
    setBagBtnLoading(true);
    try {
      const payload = {
        shipment_ids: shipment_ids || [""],
        cx_id: route?.params?.user_id || "",
        order_ids: route?.params?.orders_list || [],
        route_id: route?.params?.route_id || "",
        old_bags: route?.params?.bags || [],
        new_bags: newBags || [],
        type: route?.params?.type || "",
        order_item_ids,
        bags_disabled: route?.params?.bags_disabled,
        cx_packing_enabled: route?.params?.cx_packing_enabled,
      };
      await addToBag(payload);
      navigation.pop();
      setShowBagModal(false);
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === "object" && "response" in error
          ? (error as { response?: { data?: { message?: string } } })?.response
              ?.data?.message
          : "An error occurred";
      setBagModalError(errorMessage || "");
    } finally {
      setBagBtnLoading(false);
    }
  };

  const renderItem = useCallback(
    ({ item, section }: { item: OrderItem; section: SectionData }) => {
      const itemBackgroundColor =
        (section?.type === "CRATE" || section?.type === "PACKED_ORDERS") &&
        !item?.is_combo
          ? "tranparent"
          : "white";
      const CrateOrShipmentItem = getCrateOrShipmentItem(section?.type);
      const itemMarkingConfig =
        markingConfigs[item.marking_config_type] ||
        markingConfigs.default ||
        markingConfig;
      return (
        <View style={{ backgroundColor: itemBackgroundColor }}>
          <CrateOrShipmentItem
            info={item}
            markingConfig={itemMarkingConfig}
            fetchData={fetchData}
            imagesMandatoryForSamasya={imagesMandatoryForSamasya}
            source="SORTING"
            isOfdCp={isOfdCp}
            type={section?.type}
          />
        </View>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imagesMandatoryForSamasya],
  );

  const renderSection = ({
    section: { type, data, display_type },
  }: {
    section: SectionData;
  }) => {
    const sectionTitle =
      display_type === "CRATE"
        ? meta.crate_item_label
        : display_type === "OTHER_CRATE"
          ? meta.other_crate_label
          : display_type === "SKU_WISE_ORDER_ITEMS"
            ? meta.sku_wise_order_items_label
            : display_type === "TOP_SKU"
              ? meta.top_sku_items_label
              : display_type === "PACKED_ORDERS"
                ? meta.packed_orders
                : meta.shipment_label;
    return data.length ? (
      <View
        style={[
          styles.sectionContainer,
          type === "OTHER_CRATE" && styles.otherCrateSection,
          type === "TOP_SKU" && styles.topSkuSection,
        ]}
      >
        <CustomText variant="body_text_2" style={styles.sectionTitle}>
          {sectionTitle}
        </CustomText>
      </View>
    ) : null;
  };

  const keyExtractor = (item: OrderItem, index: number) =>
    `${item.id}_${index}`;

  const onPressCrossIcon = () => onSearchInputChange("");

  const onSearchInputChange = debounce((text: string) => {
    const filteredData = fullOrdersItem.map((data) => {
      return {
        ...data,
        items: data?.items?.filter((v: OrderItem) =>
          containText(text, v?.product_name_en),
        ),
      };
    });
    setOrdersItem(filteredData);
  }, 500);

  const shouldShowModal = () => {
    return infoItemState === "MODAL";
  };

  const containText = (text: string, item: string | undefined) => {
    if (!item) return false;
    const lowerCasedText = text?.trim().toLowerCase();
    return item?.toLowerCase().includes(lowerCasedText);
  };

  const getListHeaderComponent = () => {
    return (
      <>
        {plasticBagCount > 0 && (
          <View style={styles.plasticBagBanner}>
            <CustomText variant="body_text_2" style={styles.plasticBagText}>
              {`${plasticBagCount} पॉली बैग ज़रूरी। कृपया इस ऑर्डर को पॉली बैग में डिलीवर करें।`}
            </CustomText>
          </View>
        )}
        {route?.params?.route_name ? (
          <View style={styles.headerContainer}>
            <View style={styles.headerContent}>
              <View style={styles.avatarContainer}>
                <CustomText variant="subtitle_16" style={styles.avatarText}>
                  {route?.params?.db_name?.charAt(0).toUpperCase() || "U"}
                </CustomText>
              </View>
              <View>
                <CustomText variant="body_text_3" style={styles.routeName}>
                  {route?.params?.route_name}
                </CustomText>
                <CustomText
                  variant="subtitle_16"
                  style={{
                    color:
                      route?.params?.db_name === "UNASSIGN"
                        ? color.mediumBlacklightBlack
                        : color.black,
                  }}
                >
                  {route?.params?.db_name || "Unassigned"}
                </CustomText>
              </View>
            </View>
            <View style={styles.bagsContainer}>
              {Array.isArray(route?.params?.bags) &&
              route?.params?.status === "PACKED" &&
              route?.params?.bags.every((bag: string) => bag === "") ? (
                <NoBag />
              ) : (
                route?.params?.bags?.map((i: string, index: number) => (
                  <CustomText
                    key={`bag-${index}-${i}`}
                    variant="heading_24"
                    numberOfLines={1}
                    style={
                      index === (route?.params?.bags?.length || 0) - 1
                        ? styles.bagTextLast
                        : styles.bagText
                    }
                  >
                    {index === (route?.params?.bags?.length || 0) - 1
                      ? i
                      : `${i}, `}
                  </CustomText>
                ))
              )}
            </View>
          </View>
        ) : null}
        <InfoLines
          info={meteData?.InfoModal}
          state={(infoItemState as unknown as null | undefined) || undefined}
          onPress={onPressCut}
        />
      </>
    );
  };

  const onPressCut = async () => {
    if (buttonPress) {
      return;
    }
    setButtonPress(true);
    await markMyDeliveyModalAction("item_info_IN_SCREEN");
    await fetchData();
    setButtonPress(false);
  };

  const footerComponent = () => <View style={styles.footerSpacer} />;

  const checkItemDatailsEmpty = () => {
    return !sortUpdates.find((v) => v?.data?.length > 0);
  };

  const onSubmitConfirmModal = async () => {
    if (buttonPress) {
      return;
    }
    setButtonPress(true);
    const params = route?.params || {};
    const user_id = params.user_id || "";
    const spokeName = params.spokeName || "";
    const date = params.date || "";
    const action = `confirm_submit_modal_${user_id}_${spokeName}_${date}`;
    await markAction(action);
    fetchData();
    setVisible(false);
    setButtonPress(false);
  };

  const onSubmit = async () => {
    if (buttonPress) {
      return;
    }
    setButtonPress(true);
    await markMyDeliveyModalAction("item_info_IN_SCREEN");
    await fetchData();
    setButtonPress(false);
  };

  return (
    <View style={styles.container}>
      <HeaderWithSearch
        headertitle={`${route?.params?.user_name || ""} जी का ऑर्डर`}
        headerStyle={styles.headerTitleStyle}
        isSearchEnabled={searchEnable}
        navigation={navigation}
        goingBackHandler={navigation.goBack}
        onPressCrossIcon={onPressCrossIcon}
        onSearchInputChange={onSearchInputChange}
        searchPlaceholder={t(`${PATH}/itemSearchPlaceholder`)}
      />
      {checkItemDatailsEmpty() ? (
        <EmptySearchList />
      ) : (
        <>
          <View style={styles.listContainer}>
            <SectionList
              renderSectionHeader={renderSection}
              ListHeaderComponent={getListHeaderComponent}
              keyExtractor={keyExtractor}
              contentContainerStyle={styles.listContentContainer}
              sections={sortUpdates}
              renderItem={renderItem}
              keyboardShouldPersistTaps="handled"
              ListFooterComponent={footerComponent}
              onRefresh={onRefresh}
              refreshing={isRefreshing}
            />
          </View>
          {showSubmit ? (
            checkIfAllDefectiveForTaskPage(ordersItem) ||
            route?.params?.bags_disabled ||
            route?.params?.cx_packing_enabled ? (
              <BottomButton
                onPress={() => {
                  handleAddToBag([]);
                }}
                title={t(`${PATH}/submit`)}
              />
            ) : (
              <BottomButton
                onPress={() => {
                  if (
                    showConfirmSubmitModal &&
                    !checkCanMarkAllQuantity(fullOrdersItem)
                  ) {
                    setVisible(true);
                    return;
                  }
                  setShowBagModal(true);
                }}
                title={
                  route?.params?.bags?.length &&
                  route?.params?.bags.every((bag: string) => bag !== "")
                    ? t(`${PATH}/changeBagNumber`)
                    : t(`${PATH}/addBagNumber`)
                }
              />
            )
          ) : (
            <></>
          )}
        </>
      )}
      <AddBagNoModal
        visible={showBagModal}
        onClose={() => {
          setShowBagModal(false);
          setBagModalError("");
        }}
        item={route?.params}
        onSave={handleAddToBag}
        error={bagModalError}
        btnLoading={bagBtnLoading}
      />
      <InfoModal
        visible={shouldShowModal()}
        info={meteData?.InfoModal}
        onSubmit={onSubmit}
      />
      <MarkingInfoModal
        onSubmit={onSubmitConfirmModal}
        visible={visible}
        data={meteData?.ConfirmSubmitModal}
      />
      <Modal
        visible={isLoading}
        transparent={true}
        statusBarTranslucent
        navigationBarTranslucent
      >
        <View style={styles.loadingView}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: colors.black02,
  },
  sectionContainer: {
    padding: 12,
    backgroundColor: color.white,
    paddingLeft: 16,
  },
  sectionTitle: { color: color.black },
  topSkuSection: {
    backgroundColor: color.topSkuSectionBg,
    borderStyle: "dashed" as const,
    borderTopWidth: 1,
  },
  avatarContainer: {
    height: 35,
    width: 35,
    borderRadius: 20,
    marginRight: 8,
    backgroundColor: colors.black,
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: colors.white,
  },
  headerContainer: {
    backgroundColor: color.headerBg,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    padding: 16,
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  bagsContainer: {
    maxWidth: "50%",
    overflow: "hidden",
    flexDirection: "row",
    alignItems: "center",
  },
  bagText: {
    color: color.black,
  },
  bagTextLast: {
    color: color.black,
  },
  headerTitleStyle: {
    color: colors.white,
  },
  routeName: {
    color: colors.mediumBlacklightBlack,
    top: 5,
  },
  listContainer: {
    flex: 1,
  },
  listContentContainer: {
    marginTop: 8,
    paddingBottom: 80,
  },
  footerSpacer: {
    height: 240,
    backgroundColor: colors.white,
  },
  otherCrateSection: {
    backgroundColor: color.otherCrateBg,
    borderStyle: "dashed" as const,
    borderTopWidth: 1,
  },
  plasticBagBanner: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    // eslint-disable-next-line react-native/no-color-literals
    backgroundColor: "#FFF9E6",
    margin: 8,
    borderRadius: 6,
  },
  // eslint-disable-next-line react-native/no-color-literals
  plasticBagText: { color: "#B45309" },
});

export default SortingItemList;
